import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaApple,
  FaGooglePlay,
} from "react-icons/fa";
import ImageWithLoader from "./ImageWithLoader";
import { getLenis } from "./SmoothScroll";

const ProjectModal = ({ isOpen, onClose, project }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (isOpen) setCurrentImageIndex(0);
  }, [isOpen, project]);

  // Lock background scroll while open (stop Lenis + native overflow lock)
  useEffect(() => {
    if (!isOpen) return;
    const lenis = getLenis();
    if (lenis) lenis.stop();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      if (lenis) lenis.start();
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Esc to close + arrow keys to navigate
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, project, currentImageIndex]);

  if (!project) return null;

  const images = project.detailImages || project.images || [project.src];
  const media = project.video ? [project.video, ...images] : images;

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const isVideo = (item) =>
    item && (item.endsWith(".mp4") || item.endsWith(".webm") || item.endsWith(".ogg"));

  const isMobileApp = project.technologies?.some(
    (t) => t === "Flutter" || t === "React Native"
  );
  // Composed 4:3 posters get a full-width 4:3 frame so they fill the dialog.
  // Portrait app screenshots are contained in a fixed-height frame, and tall
  // full-page website captures get the slow scroll preview instead.
  const isPoster = !!project.poster;
  const fitContain = isMobileApp && !isPoster;

  const isValidLink = (url) => {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed === "") return false;
    // Generic placeholders we use when the app isn't published yet
    if (trimmed === "https://apps.apple.com/app") return false;
    if (trimmed === "https://play.google.com/store/apps") return false;
    return true;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(900px 500px at 30% 20%, rgba(45,212,191,0.18), transparent 60%), radial-gradient(800px 500px at 80% 80%, rgba(236,72,153,0.18), transparent 60%), rgba(4,6,13,0.85)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto overscroll-contain rounded-2xl border border-white/10"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,20,48,0.95) 0%, rgba(7,10,22,0.95) 100%)",
            }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full glass-strong border border-white/15 flex items-center justify-center text-white/80 hover:text-white hover:border-aurora-teal/40 transition-colors"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Hero media */}
            <div className="relative w-full bg-black/30 overflow-hidden">
              <div
                className={`relative flex items-center justify-center ${
                  isPoster
                    ? "w-full aspect-[4/3]"
                    : "h-[40vh] md:h-[50vh] max-h-[480px]"
                }`}
              >
                {isVideo(media[currentImageIndex]) ? (
                  <video
                    controls
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-contain"
                    key={currentImageIndex}
                  >
                    <source src={media[currentImageIndex]} type="video/mp4" />
                  </video>
                ) : (
                  <ImageWithLoader
                    src={media[currentImageIndex]}
                    alt={project.alt}
                    className={`w-full h-full ${
                      isPoster
                        ? "object-cover"
                        : fitContain
                        ? "object-contain"
                        : "object-cover modal-scroll-preview"
                    }`}
                    priority
                  />
                )}

                {/* Gradient bottom fade */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink-900 to-transparent" />

                {media.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass-strong border border-white/15 text-white flex items-center justify-center hover:border-aurora-teal/40 transition-colors"
                      aria-label="Previous"
                    >
                      <FaChevronLeft size={12} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass-strong border border-white/15 text-white flex items-center justify-center hover:border-aurora-teal/40 transition-colors"
                      aria-label="Next"
                    >
                      <FaChevronRight size={12} />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      {media.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentImageIndex(i)}
                          className={`h-1.5 rounded-full transition-all ${
                            i === currentImageIndex
                              ? "w-5 bg-aurora-teal"
                              : "w-1.5 bg-white/30 hover:bg-white/50"
                          }`}
                          aria-label={`Slide ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="p-5 md:p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-aurora-teal mb-2">
                    Case study
                  </div>
                  <h2 className="font-jamjuree text-2xl md:text-3xl font-semibold text-white leading-tight">
                    {project.title}
                  </h2>
                </div>
                {project.technologies && (
                  <div className="flex flex-wrap gap-1.5 max-w-full">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/80"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <p className="mt-5 text-sm md:text-base text-white/70 leading-relaxed">
                {project.description}
              </p>

              {/* Actions */}
              <div className="mt-7 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                {isMobileApp ? (
                  <>
                    {isValidLink(project.liveLink) ? (
                      <a
                        href={project.liveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary justify-center flex-1 sm:flex-initial"
                      >
                        <FaGooglePlay />
                        Get on Google Play
                      </a>
                    ) : (
                      <span
                        className="btn-primary justify-center flex-1 sm:flex-initial opacity-50 cursor-not-allowed"
                        aria-disabled="true"
                      >
                        <FaGooglePlay />
                        Coming soon on Google Play
                      </span>
                    )}
                    {isValidLink(project.iosLink) ? (
                      <a
                        href={project.iosLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary justify-center flex-1 sm:flex-initial"
                      >
                        <FaApple />
                        Download on App Store
                      </a>
                    ) : (
                      <span
                        className="btn-secondary justify-center flex-1 sm:flex-initial opacity-50 cursor-not-allowed"
                        aria-disabled="true"
                      >
                        <FaApple />
                        Coming soon on App Store
                      </span>
                    )}
                  </>
                ) : isValidLink(project.liveLink) ? (
                  <a
                    href={project.liveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary justify-center flex-1 sm:flex-initial"
                  >
                    <span>View live project</span>
                    <FaExternalLinkAlt size={11} />
                  </a>
                ) : project.private ? null : (
                  <span
                    className="btn-primary justify-center flex-1 sm:flex-initial opacity-50 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    <span>Coming soon</span>
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="btn-ghost justify-center flex-1 sm:flex-initial"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
